import Keycloak from 'keycloak-js';
import { getConfig } from '../config';
import { currentLanguage } from '../i18n';

const config = getConfig();

export const keycloak = new Keycloak({
  url: config.keycloakUrl,
  realm: config.keycloakRealm,
  clientId: config.keycloakClientId,
});

/** Minutes of user inactivity after which access tokens are no longer refreshed in the background. */
const ACTIVITY_WINDOW_MS = 5 * 60 * 1000;
let lastActivity = Date.now();
let initPromise: Promise<boolean> | null = null;

function markActivity() {
  lastActivity = Date.now();
}

/** Initializes keycloak-js (Authorization Code + PKCE S256). Resolves to true when authenticated. */
export function initAuth(): Promise<boolean> {
  if (!initPromise) {
    initPromise = keycloak
      .init({
        pkceMethod: 'S256',
        checkLoginIframe: false,
        responseMode: 'fragment',
        flow: 'standard',
      })
      .then((authenticated) => {
        if (authenticated) {
          ['keydown', 'pointerdown', 'wheel', 'touchstart'].forEach((type) =>
            document.addEventListener(type, markActivity, { passive: true, capture: true }),
          );
          // Silent refresh while the user is active; an idle user eventually sees the session warning.
          keycloak.onTokenExpired = () => {
            if (Date.now() - lastActivity < ACTIVITY_WINDOW_MS) {
              void keycloak.updateToken(-1).catch(() => undefined);
            }
          };
        }
        return authenticated;
      });
  }
  return initPromise;
}

/** Redirects to the Keycloak login page in the current UI language (ui_locales). */
export function login(): Promise<void> {
  return keycloak.login({ locale: currentLanguage(), redirectUri: window.location.href });
}

export function logout(): Promise<void> {
  return keycloak.logout({ redirectUri: `${window.location.origin}/` });
}

/** A valid access token (refreshed when it expires within 30 s). Redirects to login when the session is gone. */
export async function getAccessToken(): Promise<string> {
  try {
    await keycloak.updateToken(30);
  } catch {
    await login();
    throw new Error('Session expired');
  }
  if (!keycloak.token) {
    await login();
    throw new Error('Not authenticated');
  }
  return keycloak.token;
}

/** Epoch milliseconds when the SSO session (refresh token) ends, or null if unknown. */
export function sessionExpiresAt(): number | null {
  const exp = keycloak.refreshTokenParsed?.exp;
  return typeof exp === 'number' ? exp * 1000 : null;
}

/** Extends the SSO session (refresh). Returns the new session end. */
export async function extendSession(): Promise<number | null> {
  await keycloak.updateToken(-1);
  markActivity();
  return sessionExpiresAt();
}
